/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @next/next/no-img-element */
import theme from 'stories/utils/theme'
import styled from 'styled-components'
import { parseLocale } from 'lib/constants/localization'
import { GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useEffect, useRef, useState } from 'react'
import { onPageEnter } from 'lib/tracking/pageEnter'
import { trackEvent } from 'lib/tracking/analytics-service'
import {
    AnalyticsEventPlusPlanInfoImpression,
    AnalyticsEventPlusPlanSubscriptionType,
    AnalyticsEventProPlanSectionImpression,
} from 'lib/constants/AnalyticsEventType'
import { ComponentContainer } from 'components/layouts/Layouts.css'
import { Body1, Headline2, Headline5, Subtitle1, Headline3, Headline4, Body2 } from 'stories/elements/Typography/Text'
import CardContainer from 'components/elements/CardContainer'
import PlusPlanCheckout from 'components/feature/PlusPlan/PlusPlanCheckout'
import { Form } from '@unform/web'
import { FormHandles } from '@unform/core'
import StripeSubscriptionService from 'services/StripeSubscriptionService'
import _useTranslation from 'lib/hooks/_useTranslation'
import { ApiError } from 'services/ApiService'
import { StripeSubscriptionPriceResponse } from 'interfaces/StripeSubscription'
import { ErrorTypes } from 'lib/constants/ErrorTypes'
import { handleFormValidationErrors } from 'lib/validation/form'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import useImpressionTracking from 'lib/hooks/useImpressionTracking'
import { useRouter } from 'next/router'
import { getReferrerScreen } from 'lib/formatting/url'
import Input from 'components/elements/Inputs/Input'
import { toast } from 'react-toastify'
import { trackPromise } from 'react-promise-tracker'
import LoadingArea from 'components/elements/LoadingArea'
import SummaryImage from 'assets/pro-plan/summary.webp'
import { formatAmount } from 'lib/formatting/amount'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import PlusPlanCheckoutLogin from 'components/feature/PlusPlan/PlusPlanCheckoutLogin'
import Stack from 'components/elements/Stack'

const StyledSection = styled.section`
    width: 92%;
    max-width: 1400px;
    margin: 0px auto;
    padding: 125px 0;
    .promo {
        display: grid;
        gap: 20px;
        grid-template-columns: 350px 1fr;
        padding: 0 7.5%;
    }
    @media (max-width: 1440px) {
        .promo {
            padding: 0 2.5%;
        }
    }
    @media (max-width: 1050px) {
        .promo {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
        }
    }
    @media (max-width: 750px) {
        padding: 75px 0;
        .promo {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
        }
    }
    .summary {
        display: flex;
        flex-direction: column;
        gap: 15px;
        @media (max-width: 900px) {
            width: 100%;
            padding-bottom: 25px;
        }
        .total-amount {
            display: flex;
            width: 100%;
            border-top: 1px solid #ffffff33;
            padding-top: 20px;
            position: relative;
            top: 10px;
            justify-content: space-between;
        }
    }
    .fadeIn {
        animation: showCheckoutTitles 2s 1 ease-in-out forwards;
        @keyframes showCheckoutTitles {
            0% {
                opacity: 0;
                transform: translateY(-10px);
            }

            25% {
                opacity: 0;
            }

            100% {
                opacity: 1;
                transform: translateY(0px);
            }
        }
    }
`

const CheckoutChip = styled.div`
    padding: 5px 15px;
    border-radius: 8px;
    background-color: rgba(231, 240, 246, 1);
    display: flex;
    justify-content: center;
    align-items: center;
    width: fit-content;
`

const SectionTitle = styled(Headline5)`
    color: ${theme.colors.headlineDark};
`

const JoinedInput = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 35px;
    @media (max-width: 750px) {
        grid-template-columns: 1fr;
        gap: 30px;
    }
`
const FormWrapper = styled(Form)`
    display: grid;
    padding-top: 20px;
    gap: 30px;
`

const PlusContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 10px;
    height: fit-content;

    padding: 10px 10px 15px 10px;
    background: linear-gradient(0deg, rgba(10, 10, 10, 0.05), rgba(10, 10, 10, 0.05)),
        linear-gradient(0deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.15));
    border: 1px solid #ffffff1a;
    border-radius: ${theme.borderRadius.normal};
`

interface FormData {
    lastName: string
    firstName: string
    email: string
}

const loadingAreas = {
    paymentOptions: 'paymentOptions',
}

const VariantPlusPlanCheckoutPage = () => {
    const [plusPlanPrice, setPlusPlanPrice] = useState<StripeSubscriptionPriceResponse>()
    const [productId, setProductId] = useState<string | null>(null)
    const [wasTypingEventSend, setWasTypingEventSend] = useState(false)

    const subscriptionFormRef = useRef<FormHandles>(null)
    const titleRef = useRef<HTMLDivElement>(null)
    const router = useRouter()
    const { countryCode, interfaceLanguageCode } = parseLocale(router.locale)
    const referrerScreen = getReferrerScreen(router)
    const { t } = _useTranslation('plusPlan')
    const { t: tCommon } = _useTranslation('common')
    const { t: tAuth } = _useTranslation('auth')
    const md = useMedia(deviceSize.md)

    const bannerRef = useRef<HTMLDivElement>(null)

    const authenticatedUser = useSelector((state: AppState) => state.user)

    useImpressionTracking(bannerRef, AnalyticsEventProPlanSectionImpression, { referrerScreen, value: 'checkout' })

    useImpressionTracking(titleRef, AnalyticsEventPlusPlanInfoImpression, { value: 'infoCheckout', referrerScreen })

    useEffect(() => {
        onPageEnter()

        trackPromise(
            StripeSubscriptionService.listProPlanProducts(countryCode)
                .then((products) => {
                    const plusPlanProduct = products.find((product) => product.identifier.startsWith('pro_plan'))
                    if (!plusPlanProduct) {
                        toast.error(t('plusPlan/checkoutFormStripeErrorMessage'))
                        return
                    }

                    setPlusPlanPrice(plusPlanProduct?.prices[0])
                    setProductId(plusPlanProduct.productId)
                })
                .catch((error) => {
                    if (error instanceof ApiError) {
                        error.handleUnknownError(tCommon, 'stripeSubscriptionService.listPrices')
                    } else {
                        throw error
                    }
                }),
            loadingAreas.paymentOptions
        )
    }, [tCommon, countryCode, t])

    const returnCustomerInformation = () => {
        subscriptionFormRef.current!.setErrors({})

        const { email, firstName, lastName } = subscriptionFormRef.current!.getData() as FormData

        if (!firstName) {
            subscriptionFormRef.current!.setFieldError('firstName', tCommon('common/formValidationRequiredError'))
        }

        if (!lastName) {
            subscriptionFormRef.current!.setFieldError('lastName', tCommon('common/formValidationRequiredError'))
        }

        if (!email) {
            subscriptionFormRef.current!.setFieldError('email', tCommon('common/formValidationRequiredError'))
        }

        if (!firstName || !lastName || !email) return null

        const customerInformation = {
            name: `${firstName} ${lastName}`,
            email,
            userUuid: authenticatedUser?.uuid ?? null,
        }

        return customerInformation
    }

    const createSubscriptionCustomer = () => {
        const customerInformation = returnCustomerInformation()
        if (!customerInformation) return null

        return StripeSubscriptionService.createSubscriptionAndCustomer(customerInformation, interfaceLanguageCode)
            .then((subscription) => {
                return subscription
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    if (error.type === ErrorTypes.FormValidation) {
                        handleFormValidationErrors(tCommon, error, subscriptionFormRef.current!)
                    } else {
                        error.handleUnknownError(tCommon, 'stripeSubscriptionService.createSubscriptionAndCustomer')
                    }
                } else {
                    throw error
                }
                return null
            })
    }

    const onInputTypeStart = () => {
        if (wasTypingEventSend) return
        trackEvent(AnalyticsEventPlusPlanSubscriptionType, { referrerScreen })
        setWasTypingEventSend(true)
    }

    const Headline = md ? Headline3 : Headline2

    return (
        <ComponentContainer color={theme.colors.transparent} id="checkout">
            <StyledSection>
                <Subtitle1
                    className="fadeIn"
                    color={theme.colors.white}
                    textAlign="center"
                    marginBottom={10}
                    fontWeight={500}
                    fontFamily="var(--font-inter)"
                >
                    {t('plusPlan/buttonCTA')}
                </Subtitle1>
                <Headline className="fadeIn" color={theme.colors.white} textAlign="center" marginBottom={50}>
                    {t('plusPlan/checkoutSectionSubscribeNowHeadline')}
                </Headline>
                <div className="promo" ref={bannerRef}>
                    <div className="summary">
                        <Headline4 fontFamily="var(--font-inter)" color={theme.colors.white}>
                            {t('plusPlan/checkoutSummaryLabel')}
                        </Headline4>
                        <PlusContainer>
                            <img
                                src={SummaryImage.src}
                                width={SummaryImage.width}
                                height={SummaryImage.height}
                                alt="Summary"
                            />
                            <Body1
                                fontSize={18}
                                color={theme.colors.white}
                                fontFamily="var(--font-inter)"
                                textAlign="center"
                            >
                                {t('plusPlan/checkoutYearlySubscriptionLabel')}
                            </Body1>
                            <Body1
                                color={`${theme.colors.white}99`}
                                fontFamily="var(--font-inter)"
                                fontWeight={400}
                                textAlign="center"
                            >
                                {t('plusPlan/checkoutUnlimitedAccessProFeature')}
                            </Body1>
                        </PlusContainer>
                        <div className="total-amount">
                            <Body1 fontFamily="var(--font-inter)" color={theme.colors.white}>
                                {t('plusPlan/checkoutTotalLabel')}
                            </Body1>
                            {plusPlanPrice ? (
                                <Body1 fontFamily="var(--font-inter)" color={theme.colors.white}>
                                    {formatAmount(plusPlanPrice.unitAmount, plusPlanPrice.currency)}
                                </Body1>
                            ) : null}
                        </div>
                    </div>

                    <CardContainer
                        customBorder={`1px solid ${theme.colors.lightWhite}`}
                        backgroundColor={theme.colors.white}
                        padding={md ? 20 : 40}
                        heightFitContent
                    >
                        <Stack>
                            <CheckoutChip>
                                <Body2 fontFamily="var(--font-inter)" fontWeight={400} color={'rgba(160, 173, 181, 1)'}>
                                    {t('plusPlan/checkoutLabel').toUpperCase()}
                                </Body2>
                            </CheckoutChip>
                            <SectionTitle marginBottom={10} ref={titleRef}>
                                {!authenticatedUser
                                    ? `1. ${tAuth('auth/registerWithKnowunityAccountText')}`
                                    : `2. ${t('plusPlan/checkoutFormTitle')}`}
                            </SectionTitle>
                        </Stack>
                        {authenticatedUser ? (
                            <>
                                <FormWrapper
                                    ref={subscriptionFormRef}
                                    onSubmit={() => {}}
                                    initialData={{ email: authenticatedUser.email }}
                                >
                                    <JoinedInput>
                                        <Input
                                            name="firstName"
                                            placeholder={t('plusPlan/checkoutFormFirstNamePlaceholder')}
                                            label={t('plusPlan/checkoutFormFirstNameLabel')}
                                            noMargin
                                            onUpdate={onInputTypeStart}
                                        />
                                        <Input
                                            name="lastName"
                                            placeholder={t('plusPlan/checkoutFormLastNamePlaceholder')}
                                            label={t('plusPlan/checkoutFormLastNameLabel')}
                                            noMargin
                                        />
                                    </JoinedInput>
                                    <Input
                                        name="email"
                                        placeholder={t('plusPlan/checkoutFormEmailPlaceholder')}
                                        label={t('plusPlan/checkoutFormEmailLabel')}
                                        noMargin
                                    />
                                </FormWrapper>

                                <LoadingArea area={loadingAreas.paymentOptions}>
                                    {plusPlanPrice && productId ? (
                                        <PlusPlanCheckout
                                            price={plusPlanPrice}
                                            productId={productId}
                                            returnCustomerInformation={returnCustomerInformation}
                                            createSubscriptionCustomer={createSubscriptionCustomer}
                                        />
                                    ) : null}
                                </LoadingArea>
                            </>
                        ) : (
                            <PlusPlanCheckoutLogin />
                        )}
                    </CardContainer>
                </div>
            </StyledSection>
        </ComponentContainer>
    )
}

export const getStaticProps: GetStaticProps = async (props) => ({
    props: {
        ...(await serverSideTranslations(parseLocale(props.locale).interfaceLanguageCode, ['common'])),
    },
})

export default VariantPlusPlanCheckoutPage
